/**
 * P-6+ Scan-Code-Resolver.
 *
 * Versteht drei Familien:
 *   1. `platzwart://<kind>/<id>` -- QR-Sticker (unsere eigenen)
 *        kind: compartment | article | asset
 *   2. `hl:v1:compartment:<code>` -- Legacy-Format (abwärtskompatibel)
 *   3. Reine Zahlen/Alphanumerik -- EAN, Fach-Code, Asset-Seriennummer
 *
 * Die Auflösung schlägt gegen Master-API an (articles, compartments,
 * assets) und liefert eine einheitliche Struktur:
 *
 *   { ok: true, kind, entity, label, subtitle, suggestedFlows }
 *   { ok: false, error, raw }
 */

import { rpc } from "./rpc";

const PLATZ_RE = /^platzwart:\/\/([a-z_]+)\/([A-Za-z0-9_-]+)$/i;
const LEGACY_COMP_RE = /^hl:v1:compartment:(.+)$/i;

function suggestions(kind, entity) {
    switch (kind) {
        case "compartment":
            return [
                { label: "Entnahme", to: "/flows/entnahme", prefill: { compartmentId: entity.id } },
                { label: "Umlagerung", to: "/flows/umlagerung", prefill: { compartmentId: entity.id } },
                { label: "Einlagern", to: "/flows/eingang", prefill: { compartmentId: entity.id } },
                { label: "Inventur-Zählung", to: "/flows/inventory-count", prefill: { compartmentId: entity.id } },
            ];
        case "article":
            return [
                { label: "Entnahme", to: "/flows/entnahme", prefill: { articleId: entity.id } },
                { label: "Korrektur", to: "/flows/korrektur", prefill: { articleId: entity.id } },
                { label: "Verlauf anzeigen", to: `/verlauf?article=${entity.id}` },
            ];
        case "asset":
            if (entity.status === "out") {
                return [{ label: "Rückgabe", to: "/flows/rueckgabe", prefill: { assetId: entity.id } }];
            }
            return [
                { label: "Ausleihe", to: "/flows/ausleihe", prefill: { assetId: entity.id } },
                { label: "Rückgabe", to: "/flows/rueckgabe", prefill: { assetId: entity.id } },
            ];
        default:
            return [];
    }
}

// Der Continuous-Scanner liefert denselben Code mehrfach pro Sekunde;
// ohne Cache lädt jede Auflösung die kompletten Master-Listen erneut.
const CACHE_TTL_MS = 30_000;
const _listCache = new Map(); // path -> { at, data }

async function cachedList(path) {
    const hit = _listCache.get(path);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.data;
    const r = await rpc.get(path);
    if (!r.ok) return null;
    _listCache.set(path, { at: Date.now(), data: r.data });
    return r.data;
}

async function fetchEntity(kind, id) {
    switch (kind) {
        case "compartment": {
            const data = await cachedList("/master/compartments");
            return data?.find((c) => c.id === id || c.code === id)
                || data?.find((c) => c.scan_number === id) || null;
        }
        case "article": {
            const data = await cachedList("/master/articles");
            return data?.find((a) => a.id === id || a.ean === id) || null;
        }
        case "asset": {
            const data = await cachedList("/master/assets");
            return data?.find((a) => a.id === id || a.asset_tag === id || a.serial_number === id) || null;
        }
        default:
            return null;
    }
}

function labelFor(kind, entity) {
    if (!entity) return { label: "Unbekannt", subtitle: "" };
    switch (kind) {
        case "compartment":
            return {
                label: entity.code || entity.name || entity.id.slice(0, 8),
                subtitle: entity.name ? `Fach · ${entity.name}` : "Fach",
            };
        case "article":
            return {
                label: entity.name,
                subtitle: [entity.type, entity.ean].filter(Boolean).join(" · "),
            };
        case "asset":
            return {
                label: entity.asset_tag || entity.serial_number || entity.id.slice(0, 8),
                subtitle: `Ausleihgegenstand · ${entity.status || ""}`.trim(),
            };
        default:
            return { label: entity.id, subtitle: "" };
    }
}

/**
 * Parse raw scan text into a structured descriptor without calling the
 * network. Returns {kind, id} if known, else {kind:'raw', value}.
 */
export function parseCode(raw) {
    const v = (raw || "").trim();
    const m1 = v.match(PLATZ_RE);
    if (m1) return { kind: m1[1].toLowerCase(), id: m1[2] };
    const m2 = v.match(LEGACY_COMP_RE);
    if (m2) return { kind: "compartment", id: m2[1] };
    if (/^\d{8,14}$/.test(v)) return { kind: "article", id: v }; // EAN
    return { kind: "raw", value: v };
}

/**
 * Resolve raw scan text to a concrete entity. Tries multiple kinds
 * for unknown raw inputs (compartment code, then article EAN, then
 * asset serial).
 */
export async function resolveCode(raw) {
    const parsed = parseCode(raw);
    const tries =
        parsed.kind === "raw"
            ? ["compartment", "article", "asset"].map((k) => ({ kind: k, id: parsed.value }))
            : [{ kind: parsed.kind, id: parsed.id }];

    for (const t of tries) {
        const entity = await fetchEntity(t.kind, t.id);
        if (entity) {
            const { label, subtitle } = labelFor(t.kind, entity);
            return {
                ok: true,
                kind: t.kind,
                entity,
                label,
                subtitle,
                suggestedFlows: suggestions(t.kind, entity),
            };
        }
    }
    return {
        ok: false,
        error: "Code nicht zugeordnet.",
        raw,
    };
}
